class PoloLocationFinder extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.abortController = new AbortController();

    this.dragState = {
      pointerId: null,
      isPointerDown: false,
      isDragging: false,
      startX: 0,
      scrollLeft: 0,
      suppressClick: false
    };
    this.elements = this.getElements();
    this.config = this.getConfig();
    this.isSearching = false;
    this.searchButtonOriginalLabel = '';
    this.cepCache = new Map();
    this.currentPoloId = this.normalizeId(this.dataset.currentPoloId || '');
    this.allPolos = this.getPolos();
    this.priorityRules = this.getPriorityRules();

    if (!this.allPolos.length) {
      this.resetSelect(this.elements.ufSelect, this.config.ufPlaceholder, true);
      this.resetSelect(this.elements.citySelect, this.config.cityPlaceholder, true);
      this.resetSelect(this.elements.poloSelect, this.config.poloPlaceholder, true);
      if (this.elements.searchButton) this.elements.searchButton.disabled = true;
      this.setMessage(this.config.noDataMessage);
      return;
    }

    const restoredFormState = this.getFormState();
    this.populateUfs();
    this.syncRestoredFormState(restoredFormState);
    this.updateButtonState();
    this.bindEvents();
    this.setViewActive(this.elements.searchView);
    this.setViewInactive(this.elements.resultsView);
    this.hideBelowResults();
    this.scheduleRestoredFormStateSync();

    if (this.config.autoCurrentPolo && this.currentPoloId) {
      this.searchCurrentPolo();
    }
  }

  disconnectedCallback() {
    if (this.abortController) {
      this.abortController.abort();
    }
    if (this.dragClickTimer) {
      window.clearTimeout(this.dragClickTimer);
    }
    if (this.formStateSyncFrame) {
      window.cancelAnimationFrame(this.formStateSyncFrame);
      this.formStateSyncFrame = null;
    }
    if (this.formStateSyncTimer) {
      window.clearTimeout(this.formStateSyncTimer);
      this.formStateSyncTimer = null;
    }
    this.clearViewTransition();
    this.initialized = false;
  }

  getElements() {
    return {
      config: this.querySelector('[data-polo-location-config]'),
      data: this.querySelector('[data-polo-location-data]'),
      priorityRules: this.querySelector('[data-polo-priority-rules]'),
      ufSelect: this.querySelector('[data-uf-select]'),
      citySelect: this.querySelector('[data-city-select]'),
      poloSelect: this.querySelector('[data-polo-select]'),
      cepInput: this.querySelector('[data-cep-input]'),
      searchButton: this.querySelector('[data-search-button]'),
      newSearchButton: this.querySelector('[data-new-search]'),
      searchView: this.querySelector('[data-search-view]'),
      resultsView: this.querySelector('[data-results-view]'),
      resultsTrack: this.querySelector('[data-results-track]'),
      resultsDots: this.querySelector('[data-results-dots]'),
      resultsTitle: this.querySelector('[data-results-title]'),
      searchMessage: this.querySelector('[data-search-message]'),
      resultsMessage: this.querySelector('[data-results-message]'),
      belowResultsView: this.querySelector('[data-below-results-view]'),
      belowResultsTitle: this.querySelector('[data-below-results-title]'),
      belowResultsGrid: this.querySelector('[data-below-results-grid]'),
      belowResultsMessage: this.querySelector('[data-below-results-message]')
    };
  }

  getConfig() {
    const defaults = {
      autoCurrentPolo: true,
      resultsMode: 'panel',
      showResultsBelow: false,
      radiusKm: 5,
      minResults: 3,
      maxResults: 6,
      ufPlaceholder: 'Selecione o Estado',
      cityPlaceholder: 'Selecione a Cidade',
      poloPlaceholder: 'Selecione a Unidade ou Polo',
      resultsTitlePlural: 'Encontramos [count] unidades perto de voce',
      resultsTitleSingular: 'Encontramos 1 unidade perto de voce',
      relatedTitlePlural: 'Outras [count] unidades perto deste polo',
      relatedTitleSingular: 'Outra unidade perto deste polo',
      cardButtonLabel: 'Saiba mais',
      generateLinkLabel: 'Gerar link',
      copyLinkLabel: 'Copiar link',
      shareLinkLabel: 'Compartilhar link',
      generatedLinkPlaceholder: 'Clique em gerar link',
      linkCopiedMessage: 'Link copiado',
      emptyTitle: 'Nao encontramos unidades',
      emptyMessage: 'Tente buscar por outro estado, cidade, polo ou CEP.',
      noDataMessage: 'Cadastre polos em shop.metafields.custom.polos para habilitar a busca.'
    };

    try {
      const parsed = JSON.parse(this.elements.config ? this.elements.config.textContent : '{}');
      const config = Object.assign({}, defaults, parsed);
      const normalizeInteger = (value, fallback, min, max) => {
        const number = Number.parseInt(value, 10);
        if (!Number.isFinite(number)) return fallback;
        return Math.min(Math.max(number, min), max);
      };

      config.maxResults = normalizeInteger(config.maxResults, defaults.maxResults, 2, 12);
      config.minResults = normalizeInteger(config.minResults, defaults.minResults, 2, config.maxResults);
      if (!parsed.resultsMode) {
        config.resultsMode = config.showResultsBelow ? 'below' : defaults.resultsMode;
      }
      if (!['panel', 'below', 'link_generator'].includes(config.resultsMode)) {
        config.resultsMode = defaults.resultsMode;
      }
      config.showResultsBelow = config.resultsMode === 'below';

      return config;
    } catch (error) {
      return defaults;
    }
  }

  getPolos() {
    let parsed = [];

    try {
      parsed = JSON.parse(this.elements.data ? this.elements.data.textContent : '[]');
    } catch (error) {
      parsed = [];
    }

    return parsed
      .map((polo) => this.normalizePolo(polo))
      .filter((polo) => polo.id && polo.name);
  }

  getPriorityRules() {
    let parsed = [];

    try {
      parsed = JSON.parse(this.elements.priorityRules ? this.elements.priorityRules.textContent : '[]');
    } catch (error) {
      parsed = [];
    }

    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((rule) => ({
        uf: this.normalize(rule.uf),
        poloIds: Array.isArray(rule.poloIds)
          ? rule.poloIds.map((id) => this.normalizeId(id)).filter(Boolean)
          : []
      }))
      .filter((rule) => rule.uf && rule.poloIds.length);
  }

  bindEvents() {
    const {
      ufSelect,
      citySelect,
      poloSelect,
      cepInput,
      searchButton,
      newSearchButton,
      resultsTrack
    } = this.elements;

    const signal = this.abortController.signal;

    if (ufSelect) {
      ufSelect.addEventListener('change', () => {
        this.populateCities(ufSelect.value);
        if (cepInput) cepInput.value = '';
        this.updateButtonState();
      }, { signal });
    }

    if (citySelect) {
      citySelect.addEventListener('change', () => {
        this.populatePolos(ufSelect.value, citySelect.value);
        if (cepInput) cepInput.value = '';
        this.updateButtonState();
      }, { signal });
    }

    if (poloSelect) {
      poloSelect.addEventListener('change', () => {
        if (cepInput) cepInput.value = '';
        this.updateButtonState();
      }, { signal });
    }

    if (cepInput) {
      cepInput.value = this.formatCep(cepInput.value);

      cepInput.addEventListener('beforeinput', (event) => {
        if (!this.shouldAllowCepInput(event, cepInput)) {
          event.preventDefault();
        }
      }, { signal });

      cepInput.addEventListener('input', () => {
        const formattedCep = this.formatCep(cepInput.value);
        if (cepInput.value !== formattedCep) {
          cepInput.value = formattedCep;
        }
        this.updateButtonState();
      }, { signal });

      cepInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && searchButton && !searchButton.disabled) {
          event.preventDefault();
          this.handleSearch();
        }
      }, { signal });
    }

    if (searchButton) {
      searchButton.addEventListener('click', () => this.handleSearch(), { signal });
    }

    if (newSearchButton) {
      newSearchButton.addEventListener('click', () => this.showSearchView(), { signal });
    }

    if (resultsTrack) {
      resultsTrack.addEventListener('scroll', () => {
        window.requestAnimationFrame(() => this.updateDots());
      }, { passive: true, signal });
      this.bindTrackDrag(resultsTrack, signal);
    }

    window.addEventListener('resize', () => {
      window.requestAnimationFrame(() => this.renderDots());
    }, { signal });

    window.addEventListener('pageshow', () => {
      this.scheduleRestoredFormStateSync();
    }, { signal });
  }

  normalize(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  normalizeId(value) {
    return String(value || '').trim();
  }

  onlyDigits(value) {
    return String(value || '').replace(/\D/g, '');
  }

  formatCep(value) {
    const digits = this.onlyDigits(value).slice(0, 8);

    if (digits.length > 5) {
      return digits.slice(0, 5) + '-' + digits.slice(5);
    }

    return digits;
  }

  getSelectedDigitCount(input) {
    if (!input || typeof input.selectionStart !== 'number' || typeof input.selectionEnd !== 'number') {
      return 0;
    }

    return this.onlyDigits(input.value.slice(input.selectionStart, input.selectionEnd)).length;
  }

  shouldAllowCepInput(event, input) {
    if (!event || event.inputType !== 'insertText') {
      return true;
    }

    const insertedDigits = this.onlyDigits(event.data || '');
    if (!insertedDigits || insertedDigits.length !== String(event.data || '').length) {
      return false;
    }

    const currentDigits = this.onlyDigits(input ? input.value : '').length;
    const selectedDigits = this.getSelectedDigitCount(input);

    return currentDigits - selectedDigits + insertedDigits.length <= 8;
  }

  normalizeCoordinate(value, min, max) {
    if (value === null || typeof value === 'undefined') return null;

    const raw = String(value).replace(',', '.').trim();
    if (!raw) return null;

    const number = Number(raw);

    return Number.isFinite(number) && number >= min && number <= max ? number : null;
  }

  normalizePolo(polo) {
    const id = this.normalizeId(polo.id || polo.handle || '');

    return {
      id,
      handle: polo.handle || '',
      name: String(polo.name || id),
      street: String(polo.street || ''),
      neighborhood: String(polo.neighborhood || ''),
      city: String(polo.city || ''),
      uf: String(polo.uf || '').toUpperCase(),
      cep: String(polo.cep || ''),
      cepDigits: this.onlyDigits(polo.cep || ''),
      latitude: this.normalizeCoordinate(polo.latitude, -90, 90),
      longitude: this.normalizeCoordinate(polo.longitude, -180, 180),
      url: String(polo.url || '')
    };
  }

  uniqueSorted(values) {
    const seen = new Set();

    return values
      .filter((value) => {
        const clean = String(value || '').trim();
        const key = this.normalize(clean);
        if (!clean || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }

  resetSelect(select, placeholder, disabled) {
    if (!select) return;
    select.innerHTML = '';

    const option = document.createElement('option');
    option.value = '';
    option.textContent = placeholder;
    select.appendChild(option);

    select.value = '';
    select.disabled = Boolean(disabled);
  }

  appendOption(select, value, label) {
    const option = document.createElement('option');
    option.value = value;
    option.textContent = label || value;
    select.appendChild(option);
  }

  getFormState() {
    const { ufSelect, citySelect, poloSelect, cepInput } = this.elements;

    return {
      uf: ufSelect ? ufSelect.value : '',
      city: citySelect ? citySelect.value : '',
      polo: poloSelect ? poloSelect.value : '',
      cep: cepInput ? cepInput.value : ''
    };
  }

  selectHasValue(select, value) {
    if (!select || !value) return false;

    return Array.from(select.options).some((option) => option.value === value);
  }

  restoreSelectValue(select, value) {
    if (!this.selectHasValue(select, value)) return false;

    select.value = value;
    return true;
  }

  syncRestoredFormState(restoredState = {}) {
    const { ufSelect, citySelect, poloSelect, cepInput } = this.elements;
    const currentState = this.getFormState();
    const uf = restoredState.uf || currentState.uf;
    const city = restoredState.city || currentState.city;
    const polo = restoredState.polo || currentState.polo;
    const cep = restoredState.cep || currentState.cep;

    if (cepInput && cep) {
      cepInput.value = this.formatCep(cep);
    }

    if (!ufSelect) {
      this.updateButtonState();
      return;
    }

    if (uf && this.restoreSelectValue(ufSelect, uf)) {
      this.populateCities(ufSelect.value);

      if (city && this.restoreSelectValue(citySelect, city)) {
        this.populatePolos(ufSelect.value, citySelect.value);
        if (polo) this.restoreSelectValue(poloSelect, polo);
      }
    } else if (!ufSelect.value) {
      this.resetSelect(citySelect, this.config.cityPlaceholder, true);
      this.resetSelect(poloSelect, this.config.poloPlaceholder, true);
    }

    this.updateButtonState();
  }

  scheduleRestoredFormStateSync() {
    if (this.formStateSyncFrame) {
      window.cancelAnimationFrame(this.formStateSyncFrame);
    }
    if (this.formStateSyncTimer) {
      window.clearTimeout(this.formStateSyncTimer);
    }

    const sync = () => {
      if (this.isConnected) this.syncRestoredFormState();
    };

    this.formStateSyncFrame = window.requestAnimationFrame(() => {
      this.formStateSyncFrame = null;
      sync();
    });

    this.formStateSyncTimer = window.setTimeout(() => {
      this.formStateSyncTimer = null;
      sync();
    }, 120);
  }

  populateUfs() {
    const { ufSelect } = this.elements;
    this.resetSelect(ufSelect, this.config.ufPlaceholder, false);

    this.uniqueSorted(this.allPolos.map((polo) => polo.uf)).forEach((uf) => {
      this.appendOption(ufSelect, uf, uf);
    });
  }

  populateCities(uf) {
    const { citySelect, poloSelect } = this.elements;
    this.resetSelect(citySelect, this.config.cityPlaceholder, true);
    this.resetSelect(poloSelect, this.config.poloPlaceholder, true);
    if (!uf) return;

    const cities = this.uniqueSorted(this.allPolos
      .filter((polo) => this.normalize(polo.uf) === this.normalize(uf))
      .map((polo) => polo.city));

    cities.forEach((city) => {
      this.appendOption(citySelect, city, city);
    });

    citySelect.disabled = cities.length === 0;
  }

  populatePolos(uf, city) {
    const { poloSelect } = this.elements;
    this.resetSelect(poloSelect, this.config.poloPlaceholder, true);
    if (!uf || !city) return;

    const polos = this.allPolos
      .filter((polo) => this.normalize(polo.uf) === this.normalize(uf) && this.normalize(polo.city) === this.normalize(city))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

    polos.forEach((polo) => {
      this.appendOption(poloSelect, polo.id, polo.name);
    });

    poloSelect.disabled = polos.length === 0;
  }

  hasCoordinates(polo) {
    return polo && polo.latitude !== null && polo.longitude !== null;
  }

  distanceKm(a, b) {
    if (!this.hasCoordinates(a) || !this.hasCoordinates(b)) return null;

    const earthRadius = 6371;
    const dLat = this.toRadians(b.latitude - a.latitude);
    const dLng = this.toRadians(b.longitude - a.longitude);
    const lat1 = this.toRadians(a.latitude);
    const lat2 = this.toRadians(b.latitude);
    const haversine = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

    return earthRadius * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
  }

  toRadians(value) {
    return value * Math.PI / 180;
  }

  getCepApiUrl(cep) {
    return 'https://brasilapi.com.br/api/cep/v2/' + encodeURIComponent(cep);
  }

  getCepApiCoordinates(cepData) {
    const coordinates = cepData && cepData.location && cepData.location.coordinates
      ? cepData.location.coordinates
      : null;

    if (!coordinates) return null;

    const latitude = this.normalizeCoordinate(coordinates.latitude, -90, 90);
    const longitude = this.normalizeCoordinate(coordinates.longitude, -180, 180);

    if (latitude === null || longitude === null) return null;

    return {
      id: 'cep-' + this.onlyDigits(cepData.cep || ''),
      name: String(cepData.cep || ''),
      city: String(cepData.city || ''),
      uf: String(cepData.state || '').toUpperCase(),
      latitude,
      longitude
    };
  }

  async fetchCepData(cep) {
    if (this.cepCache && this.cepCache.has(cep)) {
      return this.cepCache.get(cep);
    }

    try {
      const response = await fetch(this.getCepApiUrl(cep), {
        headers: { Accept: 'application/json' },
        signal: this.abortController ? this.abortController.signal : undefined
      });

      if (!response.ok) {
        const result = {
          data: null,
          notFound: response.status === 400 || response.status === 404
        };

        if (result.notFound && this.cepCache) this.cepCache.set(cep, result);

        return result;
      }

      const result = {
        data: await response.json(),
        notFound: false
      };

      if (this.cepCache) this.cepCache.set(cep, result);

      return result;
    } catch (error) {
      if (error && error.name === 'AbortError') throw error;
      return {
        data: null,
        notFound: false
      };
    }
  }

  addUnique(list, candidate, blockedIds) {
    if (!candidate || !candidate.id) return;
    if (blockedIds && blockedIds.has(candidate.id)) return;
    if (list.some((item) => item.id === candidate.id)) return;
    list.push(candidate);
  }

  getPriorityRule(uf) {
    const normalizedUf = this.normalize(uf);

    return this.priorityRules.find((rule) => rule.uf === normalizedUf) || null;
  }

  getPoloById(poloId, candidates) {
    const id = this.normalizeId(poloId);

    return candidates.find((polo) => this.normalizeId(polo.id) === id) || null;
  }

  sortByDistanceFrom(center, candidates) {
    if (!this.hasCoordinates(center)) return candidates.slice();

    return candidates
      .map((polo) => ({
        polo,
        distance: this.distanceKm(center, polo)
      }))
      .sort((a, b) => {
        if (a.distance === null && b.distance === null) return a.polo.name.localeCompare(b.polo.name, 'pt-BR');
        if (a.distance === null) return 1;
        if (b.distance === null) return -1;
        return a.distance - b.distance;
      })
      .map((entry) => entry.polo);
  }

  getCepDistance(cep, polo) {
    const origin = Number(this.onlyDigits(cep));
    const target = Number(this.onlyDigits(polo && polo.cepDigits ? polo.cepDigits : polo && polo.cep));

    if (!Number.isFinite(origin) || !Number.isFinite(target)) return null;

    return Math.abs(origin - target);
  }

  sortByCepProximity(cep, candidates) {
    const cepDigits = this.onlyDigits(cep);

    if (!cepDigits) {
      return candidates.slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
    }

    return candidates
      .map((polo) => ({
        polo,
        cepDistance: this.getCepDistance(cepDigits, polo)
      }))
      .sort((a, b) => {
        if (a.cepDistance === null && b.cepDistance === null) return a.polo.name.localeCompare(b.polo.name, 'pt-BR');
        if (a.cepDistance === null) return 1;
        if (b.cepDistance === null) return -1;
        if (a.cepDistance !== b.cepDistance) return a.cepDistance - b.cepDistance;
        return a.polo.name.localeCompare(b.polo.name, 'pt-BR');
      })
      .map((entry) => entry.polo);
  }

  searchByCoordinates(center) {
    if (!this.hasCoordinates(center)) return [];

    return this.getNearbyPolos(center, { includeCenter: false });
  }

  searchByCepLocality(cepData) {
    const uf = cepData && cepData.state ? String(cepData.state).toUpperCase() : '';
    const city = cepData && cepData.city ? String(cepData.city) : '';
    const cep = cepData && cepData.cep ? this.onlyDigits(cepData.cep) : '';

    if (!uf) return [];

    const sameUfPolos = this.allPolos.filter((polo) => this.normalize(polo.uf) === this.normalize(uf));
    const sameCityPolos = city
      ? sameUfPolos.filter((polo) => this.normalize(polo.city) === this.normalize(city))
      : [];
    const results = sameCityPolos.length ? sameCityPolos : sameUfPolos;

    if (results.length === 1) {
      return this.complementResults(results, results[0], new Set());
    }

    return this.sortByCepProximity(cep, results).slice(0, this.config.maxResults);
  }

  complementResults(results, center, blockedIds) {
    const list = results.slice();
    const effectiveBlockedIds = blockedIds || new Set();
    const centerUf = center ? this.normalize(center.uf) : '';
    const sameUfPolos = centerUf
      ? this.allPolos.filter((polo) => this.normalize(polo.uf) === centerUf)
      : this.allPolos;
    const priorityRule = this.getPriorityRule(centerUf);

    if (priorityRule && list.length < this.config.minResults) {
      priorityRule.poloIds.forEach((poloId) => {
        const polo = this.getPoloById(poloId, sameUfPolos);
        if (list.length < this.config.minResults) {
          this.addUnique(list, polo, effectiveBlockedIds);
        }
      });
    }

    if (list.length < this.config.minResults && center) {
      this.sortByDistanceFrom(center, sameUfPolos).forEach((polo) => {
        if (list.length < this.config.minResults) this.addUnique(list, polo, effectiveBlockedIds);
      });
    }

    if (list.length === 1) {
      sameUfPolos
        .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
        .forEach((polo) => {
          if (list.length < 2) this.addUnique(list, polo, effectiveBlockedIds);
        });
    }

    return list.slice(0, this.config.maxResults);
  }

  getNearbyPolos(center, options = {}) {
    const blockedIds = new Set(Array.isArray(options.blockedIds) ? options.blockedIds : []);
    const result = [];

    if (!center) return result;
    if (options.includeCenter) this.addUnique(result, center, blockedIds);

    if (this.hasCoordinates(center)) {
      const centerUf = this.normalize(center.uf);
      const sameUfPolos = this.allPolos.filter((polo) => this.normalize(polo.uf) === centerUf);

      this.sortByDistanceFrom(center, sameUfPolos)
        .filter((polo) => {
          if (polo.id === center.id) return false;
          const distance = this.distanceKm(center, polo);
          return distance !== null && distance <= this.config.radiusKm;
        })
        .forEach((polo) => {
          this.addUnique(result, polo, blockedIds);
        });
    }

    return this.complementResults(result, center, blockedIds);
  }

  searchByLocality() {
    const { ufSelect, citySelect, poloSelect } = this.elements;
    const uf = ufSelect ? ufSelect.value : '';
    const city = citySelect ? citySelect.value : '';
    const poloId = poloSelect ? poloSelect.value : '';

    if (!uf) return [];

    if (poloId) {
      const selected = this.allPolos.find((polo) => polo.id === poloId);
      if (this.isLinkGeneratorMode()) {
        return selected ? [selected] : [];
      }
      return this.getNearbyPolos(selected, { includeCenter: true });
    }

    const results = this.allPolos.filter((polo) => {
      if (this.normalize(polo.uf) !== this.normalize(uf)) return false;
      if (city && this.normalize(polo.city) !== this.normalize(city)) return false;
      return true;
    });

    if (results.length === 1) {
      return this.complementResults(results, results[0], new Set());
    }

    return results
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
      .slice(0, this.config.maxResults);
  }

  searchByExactCepLocally(cep) {
    const exact = this.allPolos.filter((polo) => polo.cepDigits && polo.cepDigits === cep);
    if (exact.length) return this.getNearbyPolos(exact[0], { includeCenter: true });

    return [];
  }

  searchByCepLocally(cep) {
    if (cep.length < 2) return [];

    const exact = this.searchByExactCepLocally(cep);
    if (exact.length) return exact;

    const prefixLength = cep.length >= 5 ? 5 : 2;
    const prefixed = this.allPolos.filter((polo) => polo.cepDigits && polo.cepDigits.slice(0, prefixLength) === cep.slice(0, prefixLength));

    if (prefixed.length === 1) {
      return this.getNearbyPolos(prefixed[0], { includeCenter: true });
    }

    return prefixed.slice(0, this.config.maxResults);
  }

  async searchByCep() {
    const cep = this.onlyDigits(this.elements.cepInput ? this.elements.cepInput.value : '');

    if (cep.length !== 8) return [];

    const cepData = await this.fetchCepData(cep);
    if (cepData.data) {
      const center = this.getCepApiCoordinates(cepData.data);
      const coordinateResults = this.searchByCoordinates(center);

      if (coordinateResults.length) return coordinateResults;

      const localityResults = this.searchByCepLocality(cepData.data);
      if (localityResults.length) return localityResults;
    }

    if (cepData.notFound) return this.searchByExactCepLocally(cep);

    return this.searchByCepLocally(cep);
  }

  searchCurrentPolo() {
    const current = this.allPolos.find((polo) => this.normalizeId(polo.id) === this.currentPoloId || this.normalizeId(polo.handle) === this.currentPoloId);
    if (!current) return;

    const related = this.getNearbyPolos(current, {
      includeCenter: false,
      blockedIds: [current.id]
    });
    this.renderResults(related, true);
  }

  getAddress(polo) {
    const firstLine = [polo.street, polo.neighborhood].filter(Boolean).map((part) => this.escapeHtml(part)).join(' - ');
    const secondLine = [polo.city, polo.uf].filter(Boolean).map((part) => this.escapeHtml(part)).join(' - ');
    return [firstLine, secondLine].filter(Boolean).join('<br>');
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  isLinkGeneratorMode() {
    return this.config.resultsMode === 'link_generator';
  }

  getGeneratedPoloUrl(polo) {
    if (!polo || !polo.url) return '';

    try {
      const url = new URL(polo.url, window.location.origin);
      url.searchParams.set('origin', 'polo_search');
      return url.href;
    } catch (error) {
      return '';
    }
  }

  async copyText(text) {
    if (!text) return false;

    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (error) {}
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      return document.execCommand('copy');
    } catch (error) {
      return false;
    } finally {
      textarea.remove();
    }
  }

  flashButtonLabel(button, message) {
    if (!button || !message) return;

    const label = button.querySelector('[data-action-label]');
    if (!label) return;

    const originalLabel = label.dataset.originalLabel || label.textContent;
    label.dataset.originalLabel = originalLabel;
    label.textContent = message;

    if (button.linkFeedbackTimer) {
      window.clearTimeout(button.linkFeedbackTimer);
    }

    button.linkFeedbackTimer = window.setTimeout(() => {
      label.textContent = originalLabel;
      button.linkFeedbackTimer = null;
    }, 1800);
  }

  createLinkGeneratorCard(polo) {
    const article = document.createElement('article');
    article.className = 'polo-location-finder__card polo-location-finder__card--link-generator';

    const generatedUrl = this.getGeneratedPoloUrl(polo);
    const canGenerate = Boolean(generatedUrl);
    const arrowIcon =
      '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
        '<path d="M8.45 19.57a1.35 1.35 0 0 1 .07-1.9l6.77-6.33H1.33a1.34 1.34 0 0 1 0-2.68h13.96L8.52 2.33A1.35 1.35 0 0 1 8.45.43 1.32 1.32 0 0 1 10.33.36l9.25 8.66a1.35 1.35 0 0 1 0 1.97l-9.25 8.66a1.32 1.32 0 0 1-1.88-.07Z" fill="currentColor"/>' +
      '</svg>';
    const linkIcon =
      '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
        '<path d="M7.2 12.8a1 1 0 0 1 0-1.4l4.2-4.2a1 1 0 1 1 1.4 1.4l-4.2 4.2a1 1 0 0 1-1.4 0Z" fill="currentColor"/>' +
        '<path d="M8.6 5.1 9.7 4a4.1 4.1 0 0 1 5.8 5.8l-1.1 1.1a1 1 0 0 1-1.4-1.4l1.1-1.1A2.1 2.1 0 0 0 11.1 5.4L10 6.5A1 1 0 0 1 8.6 5.1Z" fill="currentColor"/>' +
        '<path d="m11.4 14.9-1.1 1.1a4.1 4.1 0 0 1-5.8-5.8l1.1-1.1A1 1 0 1 1 7 10.5l-1.1 1.1A2.1 2.1 0 1 0 8.9 14.6l1.1-1.1a1 1 0 0 1 1.4 1.4Z" fill="currentColor"/>' +
      '</svg>';
    const shareIcon =
      '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
        '<path d="M14.5 13a3 3 0 0 0-2.24.99L7.6 11.55a3.23 3.23 0 0 0 0-3.1l4.66-2.44A3 3 0 1 0 11.5 4a3.05 3.05 0 0 0 .05.53L6.89 6.97a3 3 0 1 0 0 6.06l4.66 2.44A3 3 0 1 0 14.5 13Z" fill="currentColor"/>' +
      '</svg>';

    article.innerHTML =
      '<div class="polo-location-finder__link-summary">' +
        '<span class="polo-location-finder__pin" aria-hidden="true">' +
          '<svg viewBox="0 0 20 20" focusable="false">' +
            '<path d="M10 1.8a6.2 6.2 0 0 0-6.2 6.2c0 4.7 6.2 10.2 6.2 10.2S16.2 12.7 16.2 8A6.2 6.2 0 0 0 10 1.8Zm0 8.7A2.5 2.5 0 1 1 10 5.5a2.5 2.5 0 0 1 0 5Z" fill="currentColor"/>' +
          '</svg>' +
        '</span>' +
        '<h4 class="polo-location-finder__card-title">' + this.escapeHtml(polo.name) + '</h4>' +
        '<p class="polo-location-finder__address">' + this.getAddress(polo) + '</p>' +
      '</div>' +
      '<div class="polo-location-finder__link-tools">' +
        '<button class="polo-location-finder__generate-link" type="button" data-generate-link' + (canGenerate ? '' : ' disabled') + '>' +
          '<span>' + this.escapeHtml(this.config.generateLinkLabel) + '</span>' +
          arrowIcon +
        '</button>' +
        '<input class="polo-location-finder__generated-link" type="text" inputmode="url" readonly aria-label="' + this.escapeHtml(this.config.generatedLinkPlaceholder) + '" placeholder="' + this.escapeHtml(this.config.generatedLinkPlaceholder) + '" data-generated-link>' +
        '<div class="polo-location-finder__link-actions">' +
          '<button class="polo-location-finder__link-action" type="button" data-copy-link disabled>' +
            '<span class="polo-location-finder__link-action-icon">' + linkIcon + '</span>' +
            '<span data-action-label>' + this.escapeHtml(this.config.copyLinkLabel) + '</span>' +
          '</button>' +
          '<button class="polo-location-finder__link-action" type="button" data-share-link disabled>' +
            '<span class="polo-location-finder__link-action-icon">' + shareIcon + '</span>' +
            '<span data-action-label>' + this.escapeHtml(this.config.shareLinkLabel) + '</span>' +
          '</button>' +
        '</div>' +
      '</div>';

    const input = article.querySelector('[data-generated-link]');
    const generateButton = article.querySelector('[data-generate-link]');
    const copyButton = article.querySelector('[data-copy-link]');
    const shareButton = article.querySelector('[data-share-link]');
    const revealLink = () => {
      if (!canGenerate || !input) return '';
      input.value = generatedUrl;
      if (copyButton) copyButton.disabled = false;
      if (shareButton) shareButton.disabled = false;
      return generatedUrl;
    };

    if (generateButton) {
      generateButton.addEventListener('click', () => {
        const url = revealLink();
        if (url && input) input.select();
      });
    }

    if (copyButton) {
      copyButton.addEventListener('click', async () => {
        const url = input && input.value ? input.value : revealLink();
        if (!url) return;
        const copied = await this.copyText(url);
        if (copied) this.flashButtonLabel(copyButton, this.config.linkCopiedMessage);
      });
    }

    if (shareButton) {
      shareButton.addEventListener('click', async () => {
        const url = input && input.value ? input.value : revealLink();
        if (!url) return;

        if (navigator.share) {
          try {
            await navigator.share({
              title: polo.name,
              url
            });
            return;
          } catch (error) {
            if (error && error.name === 'AbortError') return;
          }
        }

        const copied = await this.copyText(url);
        if (copied) this.flashButtonLabel(shareButton, this.config.linkCopiedMessage);
      });
    }

    return article;
  }

  createCard(polo) {
    if (this.isLinkGeneratorMode()) {
      return this.createLinkGeneratorCard(polo);
    }

    const article = document.createElement('article');
    article.className = 'polo-location-finder__card';
    const cardLinkContent =
      '<span>' + this.escapeHtml(this.config.cardButtonLabel) + '</span>' +
      '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
        '<path d="M8.45 19.57a1.35 1.35 0 0 1 .07-1.9l6.77-6.33H1.33a1.34 1.34 0 0 1 0-2.68h13.96L8.52 2.33A1.35 1.35 0 0 1 8.45.43 1.32 1.32 0 0 1 10.33.36l9.25 8.66a1.35 1.35 0 0 1 0 1.97l-9.25 8.66a1.32 1.32 0 0 1-1.88-.07Z" fill="currentColor"/>' +
      '</svg>';
    const cardLink = polo.url
      ? '<a class="polo-location-finder__card-link" href="' + this.escapeHtml(polo.url) + '">' + cardLinkContent + '</a>'
      : '<button class="polo-location-finder__card-link polo-location-finder__card-link--disabled" type="button" disabled>' + cardLinkContent + '</button>';
    article.innerHTML =
      '<span class="polo-location-finder__pin" aria-hidden="true">' +
        '<svg viewBox="0 0 20 20" focusable="false">' +
          '<path d="M10 1.8a6.2 6.2 0 0 0-6.2 6.2c0 4.7 6.2 10.2 6.2 10.2S16.2 12.7 16.2 8A6.2 6.2 0 0 0 10 1.8Zm0 8.7A2.5 2.5 0 1 1 10 5.5a2.5 2.5 0 0 1 0 5Z" fill="currentColor"/>' +
        '</svg>' +
      '</span>' +
      '<div>' +
        '<h4 class="polo-location-finder__card-title">' + this.escapeHtml(polo.name) + '</h4>' +
        '<p class="polo-location-finder__address">' + this.getAddress(polo) + '</p>' +
      '</div>' +
      cardLink;
    return article;
  }

  getResultTitle(count, isRelated) {
    if (isRelated) {
      return count === 1
        ? this.config.relatedTitleSingular
        : this.config.relatedTitlePlural.replace('[count]', count);
    }

    return count === 1
      ? this.config.resultsTitleSingular
      : this.config.resultsTitlePlural.replace('[count]', count);
  }

  renderDots() {
    const { resultsDots, resultsTrack } = this.elements;
    if (!resultsDots || !resultsTrack) return;

    resultsDots.innerHTML = '';

    const cards = Array.from(resultsTrack.children);
    this.updateTrackDragState();

    if (cards.length <= 1) return;

    const positions = this.getTrackScrollPositions();

    if (positions.length <= 1) return;

    positions.forEach((position, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'polo-location-finder__dot' + (index === 0 ? ' is-active' : '');
      dot.addEventListener('click', () => {
        resultsTrack.scrollTo({
          left: position,
          behavior: 'smooth'
        });
      });
      resultsDots.appendChild(dot);
    });

    this.updateDots();
  }

  updateDots() {
    const { resultsDots, resultsTrack } = this.elements;
    if (!resultsDots || !resultsTrack) return;

    const dots = Array.from(resultsDots.children);
    if (!dots.length) return;

    const positions = this.getTrackScrollPositions();
    if (!positions.length) return;

    const active = positions.reduce((closestIndex, position, index) => {
      const closestDistance = Math.abs(resultsTrack.scrollLeft - positions[closestIndex]);
      const distance = Math.abs(resultsTrack.scrollLeft - position);
      return distance < closestDistance ? index : closestIndex;
    }, 0);

    dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === active);
    });
  }

  getTrackScrollPositions() {
    const { resultsTrack } = this.elements;
    if (!resultsTrack) return [];

    const cards = Array.from(resultsTrack.children);
    const maxScrollLeft = Math.max(resultsTrack.scrollWidth - resultsTrack.clientWidth, 0);
    const positions = [];

    cards.forEach((card) => {
      const position = Math.min(Math.max(card.offsetLeft - resultsTrack.offsetLeft, 0), maxScrollLeft);
      const lastPosition = positions[positions.length - 1];

      if (lastPosition === undefined || Math.abs(position - lastPosition) > 2) {
        positions.push(position);
      }
    });

    return positions;
  }

  updateTrackDragState() {
    const { resultsTrack } = this.elements;
    if (!resultsTrack) return;

    const hasScrollableCards = resultsTrack.scrollWidth > resultsTrack.clientWidth + 1;
    resultsTrack.classList.toggle('is-draggable', hasScrollableCards);
  }

  isInteractiveTrackTarget(target) {
    return Boolean(target && target.closest && target.closest('a, button, input, select, textarea, label, summary, [role="button"], [role="link"], [contenteditable="true"]'));
  }

  bindTrackDrag(track, signal) {
    track.addEventListener('pointerdown', (event) => this.handleTrackPointerDown(event), { signal });
    track.addEventListener('pointermove', (event) => this.handleTrackPointerMove(event), { signal });
    track.addEventListener('pointerup', (event) => this.handleTrackPointerUp(event), { signal });
    track.addEventListener('pointercancel', (event) => this.handleTrackPointerUp(event), { signal });
    track.addEventListener('lostpointercapture', (event) => this.handleTrackPointerUp(event), { signal });
    track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    track.addEventListener('click', (event) => this.handleTrackClick(event), { capture: true, signal });
  }

  handleTrackPointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    const track = event.currentTarget;
    if (!track || track.scrollWidth <= track.clientWidth + 1) return;
    if (this.isInteractiveTrackTarget(event.target)) return;

    this.dragState.pointerId = event.pointerId;
    this.dragState.isPointerDown = true;
    this.dragState.isDragging = false;
    this.dragState.startX = event.clientX;
    this.dragState.scrollLeft = track.scrollLeft;
    this.dragState.suppressClick = false;

    if (track.setPointerCapture) {
      track.setPointerCapture(event.pointerId);
    }
  }

  handleTrackPointerMove(event) {
    const state = this.dragState;
    if (!state || !state.isPointerDown || state.pointerId !== event.pointerId) return;

    const track = event.currentTarget;
    const deltaX = event.clientX - state.startX;
    const dragThreshold = 6;

    if (!state.isDragging) {
      if (Math.abs(deltaX) < dragThreshold) return;
      state.isDragging = true;
      track.classList.add('is-dragging');
    }

    event.preventDefault();
    track.scrollLeft = state.scrollLeft - deltaX;
  }

  handleTrackPointerUp(event) {
    const state = this.dragState;
    if (!state || !state.isPointerDown || state.pointerId !== event.pointerId) return;

    const track = event.currentTarget;
    const wasDragging = state.isDragging;

    if (track && track.releasePointerCapture && track.hasPointerCapture && track.hasPointerCapture(event.pointerId)) {
      track.releasePointerCapture(event.pointerId);
    }

    if (track) {
      track.classList.remove('is-dragging');
    }

    state.pointerId = null;
    state.isPointerDown = false;
    state.isDragging = false;
    state.suppressClick = wasDragging;

    if (this.dragClickTimer) {
      window.clearTimeout(this.dragClickTimer);
    }

    if (wasDragging) {
      this.dragClickTimer = window.setTimeout(() => {
        state.suppressClick = false;
      }, 180);
    }
  }

  handleTrackClick(event) {
    if (!this.dragState || !this.dragState.suppressClick) return;

    event.preventDefault();
    event.stopPropagation();
    this.dragState.suppressClick = false;

    if (this.dragClickTimer) {
      window.clearTimeout(this.dragClickTimer);
      this.dragClickTimer = null;
    }
  }

  renderResults(results, isRelated) {
    if (this.config.showResultsBelow) {
      this.renderBelowResults(results, isRelated, { shouldScroll: !isRelated });
      return;
    }

    this.hideBelowResults();
    this.renderPanelResults(results, isRelated);
  }

  renderPanelResults(results, isRelated) {
    const { resultsTrack, resultsDots, resultsTitle, resultsMessage } = this.elements;
    if (!resultsTrack || !resultsDots || !resultsTitle) return;

    resultsTrack.innerHTML = '';
    resultsDots.innerHTML = '';
    this.updateTrackDragState();

    if (!results.length) {
      if (resultsMessage) {
        resultsMessage.hidden = false;
        resultsMessage.textContent = this.config.emptyMessage;
      }
      resultsTitle.textContent = this.config.emptyTitle;
      if (this.isLinkGeneratorMode()) {
        this.showLinkGeneratorResultsView();
        return;
      }
      this.showResultsView();
      return;
    }

    if (resultsMessage) {
      resultsMessage.hidden = true;
      resultsMessage.textContent = '';
    }

    resultsTitle.textContent = this.getResultTitle(results.length, isRelated);
    results.forEach((polo) => {
      resultsTrack.appendChild(this.createCard(polo));
    });

    if (this.elements.resultsView) {
      this.elements.resultsView.hidden = false;
    }
    if (this.isLinkGeneratorMode()) {
      this.showLinkGeneratorResultsView(() => {
        window.requestAnimationFrame(() => this.updateDots());
      });
      window.requestAnimationFrame(() => this.renderDots());
      return;
    }
    this.showResultsView(() => {
      window.requestAnimationFrame(() => this.updateDots());
    });
    window.requestAnimationFrame(() => this.renderDots());
  }

  renderBelowResults(results, isRelated, options = {}) {
    const { belowResultsView, belowResultsTitle, belowResultsGrid, belowResultsMessage } = this.elements;

    if (!belowResultsView || !belowResultsTitle || !belowResultsGrid) {
      this.renderPanelResults(results, isRelated);
      return;
    }

    if (this.elements.resultsTrack) this.elements.resultsTrack.innerHTML = '';
    if (this.elements.resultsDots) this.elements.resultsDots.innerHTML = '';
    this.updateTrackDragState();
    this.setViewActive(this.elements.searchView);
    this.setViewInactive(this.elements.resultsView);

    belowResultsGrid.innerHTML = '';
    belowResultsTitle.textContent = results.length
      ? this.getResultTitle(results.length, isRelated)
      : this.config.emptyTitle;

    if (belowResultsMessage) {
      belowResultsMessage.hidden = Boolean(results.length);
      belowResultsMessage.textContent = results.length ? '' : this.config.emptyMessage;
    }

    results.forEach((polo) => {
      belowResultsGrid.appendChild(this.createCard(polo));
    });

    this.showBelowResults();

    if (options.shouldScroll) {
      this.scrollBelowResultsIntoView();
    }
  }

  showBelowResults() {
    const { belowResultsView } = this.elements;
    if (!belowResultsView) return;

    belowResultsView.hidden = false;
    belowResultsView.removeAttribute('aria-hidden');
    belowResultsView.removeAttribute('inert');
    belowResultsView.inert = false;
  }

  hideBelowResults() {
    const { belowResultsView, belowResultsGrid, belowResultsMessage } = this.elements;
    if (!belowResultsView) return;

    belowResultsView.hidden = true;
    belowResultsView.setAttribute('aria-hidden', 'true');
    belowResultsView.setAttribute('inert', '');
    belowResultsView.inert = true;

    if (belowResultsGrid) belowResultsGrid.innerHTML = '';
    if (belowResultsMessage) {
      belowResultsMessage.hidden = true;
      belowResultsMessage.textContent = '';
    }
  }

  scrollBelowResultsIntoView() {
    const { belowResultsView } = this.elements;
    if (!belowResultsView || belowResultsView.hidden) return;

    window.requestAnimationFrame(() => {
      belowResultsView.scrollIntoView({
        behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start'
      });
    });
  }

  prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  clearViewTransition() {
    if (this.viewTransitionTimers) {
      this.viewTransitionTimers.forEach((timer) => window.clearTimeout(timer));
    }
    this.viewTransitionTimers = [];
  }

  resetViewTransitionState() {
    const { searchView, resultsView } = this.elements;
    [searchView, resultsView].forEach((view) => {
      if (!view) return;
      view.classList.remove('is-view-entering', 'is-view-leaving');
    });
  }

  setViewActive(view) {
    if (!view) return;
    view.hidden = false;
    view.classList.remove('is-view-hidden', 'is-view-entering', 'is-view-leaving');
    view.removeAttribute('aria-hidden');
    view.removeAttribute('inert');
    view.inert = false;
  }

  setViewInactive(view) {
    if (!view) return;
    view.hidden = false;
    view.classList.remove('is-view-entering', 'is-view-leaving');
    view.classList.add('is-view-hidden');
    view.setAttribute('aria-hidden', 'true');
    view.setAttribute('inert', '');
    view.inert = true;
  }

  transitionViews(fromView, toView, onComplete) {
    if (!fromView || !toView || fromView === toView) {
      this.setViewActive(toView);
      if (onComplete) onComplete();
      return;
    }

    this.clearViewTransition();
    this.resetViewTransitionState();

    if (this.prefersReducedMotion()) {
      this.setViewInactive(fromView);
      this.setViewActive(toView);
      if (onComplete) onComplete();
      return;
    }

    fromView.hidden = false;
    toView.hidden = false;
    fromView.classList.remove('is-view-hidden', 'is-view-entering');
    toView.classList.remove('is-view-hidden', 'is-view-leaving');
    fromView.setAttribute('aria-hidden', 'true');
    fromView.setAttribute('inert', '');
    fromView.inert = true;
    toView.removeAttribute('aria-hidden');
    toView.removeAttribute('inert');
    toView.inert = false;
    fromView.classList.add('is-view-leaving');
    toView.classList.add('is-view-entering');

    window.requestAnimationFrame(() => {
      toView.classList.remove('is-view-entering');
    });

    const transitionTimer = window.setTimeout(() => {
      this.setViewInactive(fromView);
      this.setViewActive(toView);
      this.viewTransitionTimers = [];
      if (onComplete) onComplete();
    }, 240);

    this.viewTransitionTimers.push(transitionTimer);
  }

  showResultsView(onComplete) {
    this.transitionViews(this.elements.searchView, this.elements.resultsView, onComplete);
  }

  showLinkGeneratorResultsView(onComplete) {
    this.clearViewTransition();
    this.resetViewTransitionState();
    this.setViewActive(this.elements.searchView);
    this.setViewActive(this.elements.resultsView);
    if (onComplete) onComplete();
  }

  showSearchView() {
    this.hideBelowResults();
    this.transitionViews(this.elements.resultsView, this.elements.searchView, () => {
      if (this.elements.resultsMessage) {
        this.elements.resultsMessage.hidden = true;
        this.elements.resultsMessage.textContent = '';
      }
    });
  }

  setMessage(message) {
    const { searchMessage } = this.elements;
    if (!searchMessage) return;

    searchMessage.hidden = !message;
    searchMessage.textContent = message || '';
  }

  setSearchLoading(isLoading) {
    const { searchButton } = this.elements;
    this.isSearching = Boolean(isLoading);

    if (!searchButton) return;

    const label = searchButton.querySelector('span');
    if (label && !this.searchButtonOriginalLabel) {
      this.searchButtonOriginalLabel = label.textContent;
    }

    if (this.isSearching) {
      searchButton.disabled = true;
      searchButton.setAttribute('aria-busy', 'true');
      if (label) label.textContent = 'Buscando';
      return;
    }

    searchButton.removeAttribute('aria-busy');
    if (label && this.searchButtonOriginalLabel) {
      label.textContent = this.searchButtonOriginalLabel;
    }
    this.updateButtonState();
  }

  updateButtonState() {
    const cepLength = this.onlyDigits(this.elements.cepInput ? this.elements.cepInput.value : '').length;
    const hasCep = cepLength === 8;
    const hasInvalidCep = cepLength > 0 && cepLength !== 8;
    const hasUf = Boolean(this.elements.ufSelect && this.elements.ufSelect.value);

    if (this.elements.searchButton) {
      this.elements.searchButton.disabled = this.isSearching || hasInvalidCep || !(hasCep || hasUf);
    }
    if (!this.isSearching) this.setMessage('');
  }

  async handleSearch() {
    if (this.isSearching) return;

    const cep = this.onlyDigits(this.elements.cepInput ? this.elements.cepInput.value : '');
    const hasCep = cep.length === 8;

    if (cep.length > 0 && !hasCep) {
      this.setMessage('Informe um CEP com 8 digitos.');
      return;
    }

    this.setSearchLoading(true);
    if (this.config.showResultsBelow) this.hideBelowResults();

    try {
      const results = hasCep ? await this.searchByCep() : this.searchByLocality();

      if (!this.isConnected) return;

      this.renderResults(results, false);
    } catch (error) {
      if (error && error.name === 'AbortError') return;
      this.renderResults([], false);
    } finally {
      if (this.isConnected) this.setSearchLoading(false);
    }
  }
}

if (!customElements.get('polo-location-finder')) {
  customElements.define('polo-location-finder', PoloLocationFinder);
}
